import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { User } from "lucide-react";
import { Textarea } from "../../../../../../components/ui/textarea";
import Dialog from "../../../../../../components/shared/Dialog";
import Avatar from "../../../../../../components/shared/Avatar";
import Button from "../../../../../../components/shared/Button";
import BaseForm, { Field } from "../../../../../../components/shared/Form";
import SectionDetails from "../../drawers/ShiftTrade/SectionDetails";
import { calculateSections } from "../../../hooks/form/useCalculatedSections";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { createMoveToOpenShiftsSchema, type MoveToOpenShiftsFormValues } from "./zodValidation";
import useApi from "../../../../../../utils/api";
import { useAccess } from "../../../../../../utils/hooks/access";
import { useUser } from "../../../../../../utils/hooks/user";
import { renderUserName } from "../../../../../../utils/helpers/user";
import { withLoadingToast } from "../../../helpers/withLoadingToast";
import { getResourceUser } from "../../../helpers/data/groupedResourceId";
import type { CalculatedSection } from "../../drawers/ShiftTrade/types";
import type { Event } from "../../../types/Event";
import type { Resource } from "../../../types/Resource";

const FORM_ID = "move-to-open-shifts-form";

interface MoveToOpenShiftsProps {
    open: boolean;
    values: (Event & { resource?: Resource; requestType?: string }) | null;
    close: () => void;
}

function MoveToOpenShifts({ open, values, close }: MoveToOpenShiftsProps) {
    const { t } = useTranslation();
    const currentUser = useUser();
    const { authPost } = useApi();
    const { type, dispatch } = useScheduleContext();
    const { hasAccess: canCreateRequest } = useAccess("request.canCreate");

    const sections = useMemo((): CalculatedSection[] => {
        return calculateSections(values?.sections, values?.startDate) ?? [];
    }, [values]);

    const user = useMemo(() => {
        return getResourceUser(typeof values?.user === "object" ? values?.user : values?.resource);
    }, [values]);

    const requestType = values?.requestType ?? "open-shift";
    const isCallOff = requestType === "call-off";

    const schema = useMemo(() => createMoveToOpenShiftsSchema({ t }), [t]);

    const form = useForm<MoveToOpenShiftsFormValues>({
        defaultValues: { reason: "" },
        resolver: zodResolver(schema)
    });

    const onClose = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    const endpoint = useMemo(() => {
        return type === "team"
            ? `/schedules/v2/open-shift/user/${user?.id}`
            : "/my/schedules/v2/open-shift";
    }, [type, user?.id]);

    const mutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (data: any) => authPost(endpoint, { data }),
        onSuccess: () => {
            dispatch({
                type: "update-event-meta",
                payload: { id: values?.id, meta: { openRequest: true } },
            });
            toast.success(t("request-created-successfully"));
            onClose();
        },
        onError: (error: any) => {
            if (error?.response?.status === 403) {
                toast.error(t("request-not-allowed"));
                return;
            }

            if (typeof error === "string") {
                toast.error(error);
                return;
            }

            const [fieldError] = error && typeof error === "object"
                ? Object.values(error).flat()
                : [];

            toast.error(fieldError ? String(fieldError) : t("request-creation-failed"));
        },
    });

    const onFinish = useCallback((value: MoveToOpenShiftsFormValues) => {
        mutation.mutate({
            user: user?.id ?? currentUser?.id,
            type: requestType,
            namespace: "schedule",
            device: "web-application",
            data: { shift: values?.id, reason: value?.reason },
        });
    }, [mutation, user?.id, currentUser?.id, requestType, values?.id]);

    return (
        <Dialog
            open={open}
            title={isCallOff ? t("call-off") : t("move-to-open-shifts")}
            close={onClose}
            size="xl"
            footer={canCreateRequest && (
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        disabled={mutation.isPending}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        type="submit"
                        form={FORM_ID}
                        disabled={mutation.isPending}
                        loading={mutation.isPending}
                    >
                        {t("create-request")}
                    </Button>
                </div>
            )}
        >
            <div className="tw-p-1">
                {sections?.length > 0 && (
                    <SectionDetails sections={sections} />
                )}

                {type !== "home" && user && (
                    <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-border tw-rounded-lg shadow-xs tw-p-4">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <User size={19} className="tw-text-primary" />
                            <span className="tw-text-sm">{t("employee")}</span>
                        </div>
                        <div className="tw-flex tw-gap-2 tw-items-center tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-font-semibold">
                            <Avatar user={user} size="xs" textStyles="text-xs" />
                            {renderUserName(user)}
                        </div>
                    </div>
                )}

                {canCreateRequest && (
                    <BaseForm
                        id={FORM_ID}
                        form={form}
                        onFinish={onFinish}
                        className="tw-mt-3"
                    >
                        <Field name="reason" label={t("reason")} required>
                            <Textarea
                                rows={3}
                                className="tw-resize-none field-sizing-fixed"
                                placeholder={t("reason")}
                            />
                        </Field>
                    </BaseForm>
                )}
            </div>
        </Dialog>
    );
}

export default MoveToOpenShifts;
